import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { User, PaperPlaneRight, Warning, Clock } from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import { useAppServices } from "../../../hook/services";
import LoadingScreen from "../../../components/LoadingScreen";
const Dashboard = () => {
  const { locationId } = useParams();
  const AppServices = useAppServices();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState(0);
  const [devices, setDevices] = useState(0);
  const [sims, setSims] = useState(0);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { response } = await AppServices.agency.get_dashboard_data({
        query: `locationId=${locationId}`,
      });
      if (response) {
        setMessages(response.messages || []);
        setUsers(response.users || 0);
        setDevices(response.devices || 0);
        setSims(response.sims || 0);
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ KPI cards
  const kpis = [
    {
      title: "Users",
      value: users,
      icon: <PaperPlaneRight size={32} weight="duotone" />,
    },
    {
      title: "Sims",
      value: sims,
      icon: <PaperPlaneRight size={32} weight="duotone" />,
    },
    {
      title: "Devices",
      value: devices,
      icon: <PaperPlaneRight size={32} weight="duotone" />,
    },
    {
      title: "Total Messages",
      value: messages.length,
      icon: <PaperPlaneRight size={32} weight="duotone" />,
    },
    {
      title: "Delivered",
      value: messages.filter((m) => m.status?.toLowerCase() === "sent").length,
      icon: <User size={32} weight="duotone" />,
    },
    {
      title: "Failed",
      value: messages.filter((m) => m.status?.toLowerCase() === "failed")
        .length,
      icon: <Warning size={32} weight="duotone" />,
    },
    {
      title: "Pending",
      value: messages.filter((m) => m.status?.toLowerCase() === "pending")
        .length,
      icon: <Clock size={32} weight="duotone" />,
    },
  ];

  // ✅ Month + Weekday labels
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Group by Month
  const groupByMonth = (status) => {
    const counts = {};
    messages.forEach((msg) => {
      const d = new Date(msg.createdAt);
      const key = monthNames[d.getMonth()];
      if (!counts[key]) counts[key] = 0;
      if (msg.status?.toLowerCase() === status) {
        counts[key]++;
      }
    });
    return counts;
  };

  // Group by Weekday
  // Group only this week's messages by weekday
  const groupByWeekDay = (status) => {
    const counts = {};
    const today = new Date();

    // Start of this week (Sunday)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    // End of this week (Saturday)
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    messages.forEach((msg) => {
      const d = new Date(msg.createdAt);
      if (d >= startOfWeek && d <= endOfWeek) {
        const key = weekDays[d.getDay()];
        if (!counts[key]) counts[key] = 0;
        if (msg.status?.toLowerCase() === status) {
          counts[key]++;
        }
      }
    });
    return counts;
  };

  // ✅ Monthly data
  const deliveredMonthly = groupByMonth("sent");
  const failedMonthly = groupByMonth("failed");

  const monthlySeries = [
    {
      name: "Delivered",
      data: monthNames.map((m) => deliveredMonthly[m] || 0),
    },
    { name: "Failed", data: monthNames.map((m) => failedMonthly[m] || 0) },
  ];

  // ✅ Daily data (always show Sun–Sat)
  const deliveredDaily = groupByWeekDay("sent");
  const failedDaily = groupByWeekDay("failed");

  const dailySeries = [
    { name: "Delivered", data: weekDays.map((d) => deliveredDaily[d] || 0) },
    { name: "Failed", data: weekDays.map((d) => failedDaily[d] || 0) },
  ];

  // ✅ Chart options
  const baseOptions = {
    chart: { toolbar: { show: false } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth" },
    legend: { position: "top" },
  };

  const monthlyOptions = { ...baseOptions, xaxis: { categories: monthNames } };
  const dailyOptions = { ...baseOptions, xaxis: { categories: weekDays } };

  // Success Rate calculation
  const totalDelivered = messages.filter(
    (m) => m.status?.toLowerCase() === "sent"
  ).length;
  const totalFailed = messages.filter(
    (m) => m.status?.toLowerCase() === "failed"
  ).length;
  const totalAttempts = totalDelivered + totalFailed;
  const successRate =
    totalAttempts > 0 ? ((totalDelivered / totalAttempts) * 100).toFixed(1) : 0;

  // Success rate chart
  const successOptions = {
    chart: { type: "donut" },
    labels: ["Delivered", "Failed"],
    legend: { position: "bottom" },
    colors: ["#28a745", "#dc3545"],
  };

  const successSeries = [totalDelivered, totalFailed];
  if (loading) {
    return <LoadingScreen loadingText={"Loading dashboard..."} />;
  }
  return (
    <div className="container-fluid p-4 bg-light min-vh-100">
      {/* KPI Cards */}
      <div className="row g-4 mb-4">
        {kpis.map((kpi, idx) => (
          <div className="col-12 col-sm-6 col-lg-3" key={idx}>
            <div className="card shadow-sm border-0 h-100 p-3 d-flex flex-row align-items-center">
              <div className="text-primary me-3">{kpi.icon}</div>
              <div>
                <h6 className="text-muted mb-1">{kpi.title}</h6>
                <h4 className="fw-bold mb-0">{kpi.value}</h4>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="row g-4 mb-4">
        <div className="row g-4 mb-4">
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 p-3 h-100">
              <h6 className="fw-bold mb-3">Message Overview (Daily)</h6>
              <Chart
                options={dailyOptions}
                series={dailySeries}
                type="bar"
                height={280}
              />
            </div>
          </div>
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 p-3 h-100 text-center">
              <h6 className="fw-bold mb-3">Success Rate</h6>
              <Chart
                options={successOptions}
                series={successSeries}
                type="donut"
                height={280}
              />
              <h4 className="fw-bold mt-3 text-success">{successRate}%</h4>
              <p className="text-muted small">Overall delivery success</p>
            </div>
          </div>
          <div className="col-12">
            <div className="card shadow-sm border-0 p-3 h-100">
              <h6 className="fw-bold mb-3">Message Overview (Monthly)</h6>
              <Chart
                options={monthlyOptions}
                series={monthlySeries}
                type="line"
                height={280}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
